import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../api/admin/adminApi';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import '../../styles/AdminDashboard.css';

const DESIGN_STATUSES = [
  'draft',
  'submitted',
  'awaiting_design_review',
  'proof_ready',
  'awaiting_customer_approval',
  'changes_requested',
  'approved',
];

function QueueTable({ title, subtitle, rows }) {
  return (
    <section className="admin-collapsible-section" style={{ marginBottom: 20 }}>
      <div className="admin-collapsible-section__header">
        <div className="admin-collapsible-section__titles">
          <h3 className="admin-collapsible-section__title">{title}</h3>
          <p className="admin-collapsible-section__subtitle">{subtitle}</p>
        </div>
        <span className="admin-collapsible-section__badge">{rows.length}</span>
      </div>
      <div className="admin-table">
        {rows.length === 0 ? (
          <p className="admin-table-subtitle">None in this queue.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Who / source</th>
                <th>State</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row._id || row.cardId}>
                  <td>{row.reference || row.cardId || row.externalId || row._id}</td>
                  <td>{row.email || row.source || '—'}</td>
                  <td>{row.paymentStatus || row.claimState || row.fulfilmentState || '—'}</td>
                  <td>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function findingCount(findings) {
  if (!findings) return 0;
  return Object.values(findings).reduce((sum, list) => sum + (Array.isArray(list) ? list.length : 0), 0);
}

function canManageCardOrders(account) {
  if (account?.role === 'admin' || account?.staff?.superAdmin) return true;
  const permissions = account?.staff?.permissions || [];
  return account?.staff?.status === 'active' && (
    permissions.includes('orders.manage')
    || permissions.includes('fulfilment.manage')
    || permissions.includes('cards.manage')
  );
}

function CardOrderEditor({ order, draft, disabled, onChange, onSave }) {
  return (
    <div className="admin-inperson-distribution-form" style={{ marginTop: 0 }}>
      <label>
        Design status
        <select
          value={draft.designStatus}
          disabled={disabled}
          onChange={(e) => onChange({ ...draft, designStatus: e.target.value })}
        >
          {DESIGN_STATUSES.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
      </label>
      <label>
        Proof image URL
        <input
          value={draft.proofImageUrl}
          disabled={disabled}
          onChange={(e) => onChange({ ...draft, proofImageUrl: e.target.value })}
          placeholder="https://…"
        />
      </label>
      <button type="button" className="btn" disabled={disabled} onClick={() => onSave(order, draft)}>
        {disabled ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

export default function AdminFulfilmentPage() {
  const { account, registerShellMeta, setRefreshHandler } = useOutletContext() || {};
  const isDesktop = useMediaQuery('(min-width: 769px)');
  const [queue, setQueue] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [cardOrders, setCardOrders] = useState([]);
  const [orderDrafts, setOrderDrafts] = useState({});
  const [savingOrderId, setSavingOrderId] = useState('');
  const [error, setError] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const manageOrders = canManageCardOrders(account);

  const load = useCallback(async () => {
    setError(null);
    const [queueRes, reconRes, ordersRes] = await Promise.all([
      adminApi.getFulfilmentQueue(),
      adminApi.getCardReconciliation().catch(() => ({ data: null })),
      adminApi.listCardOrders().catch(() => ({ orders: [] })),
    ]);
    setQueue(queueRes.data || null);
    setReconciliation(reconRes.data || null);
    const orders = ordersRes.orders || [];
    setCardOrders(orders);
    setOrderDrafts((prev) => {
      const next = { ...prev };
      orders.forEach((order) => {
        const id = String(order._id);
        next[id] = {
          designStatus: next[id]?.designStatus || order.designStatus || 'draft',
          proofImageUrl: next[id]?.proofImageUrl ?? order.proof?.imageUrl ?? '',
        };
      });
      return next;
    });
  }, []);

  useEffect(() => {
    registerShellMeta?.({
      title: 'Fulfilment',
      subtitle: 'Shopify and ledger queues — programme, ship, review',
    });
    setRefreshHandler?.(load);
    load().catch((err) => setError(err.message));
  }, [load, registerShellMeta, setRefreshHandler]);

  const repair = async () => {
    if (!window.confirm('Repair only the unambiguous ledger findings? Ambiguous ownership is left for a person.')) {
      return;
    }
    setRepairing(true);
    try {
      await adminApi.repairCardReconciliation();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRepairing(false);
    }
  };

  const saveCardOrder = async (order, draft) => {
    const id = String(order._id);
    setSavingOrderId(id);
    setError(null);
    try {
      const proofImageUrl = String(draft.proofImageUrl || '').trim();
      await adminApi.patchCardOrder(id, {
        designStatus: draft.designStatus,
        ...(proofImageUrl && proofImageUrl !== (order.proof?.imageUrl || '') ? { proofImageUrl } : {}),
      });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingOrderId('');
    }
  };

  const findings = findingCount(reconciliation?.findings);

  return (
    <div className="admin-dashboard">
      <div className="analytics-section">
        <div className="admin-table-header">
          <div>
            <h2>Fulfilment console</h2>
            <p className="admin-table-subtitle">
              Physical-card payment stays on Shopify. This is the internal queue across paid orders,
              cards that still need programming, and ledger rows flagged for review.
            </p>
          </div>
          <div className="admin-table-actions">
            <button type="button" className="btn" onClick={load}>
              Refresh
            </button>
            <button type="button" className="btn" onClick={repair} disabled={repairing}>
              {repairing ? 'Repairing…' : 'Repair safe findings'}
            </button>
          </div>
        </div>

        {error ? <p role="alert">{error}</p> : null}

        {reconciliation ? (
          <p className="admin-table-subtitle">
            Reconciliation findings: {findings}. Safe repairs never decide contested ownership.
          </p>
        ) : null}

        {queue ? (
          <>
            <QueueTable
              title="Awaiting payment"
              subtitle="Shopify draft or unpaid order — do not programme yet"
              rows={queue.awaitingPayment || []}
            />
            <QueueTable
              title="Ready to programme"
              subtitle="Paid or free replacement — write NFC and ship"
              rows={queue.readyToProgramme || []}
            />
            <QueueTable
              title="Needs programming"
              subtitle="Ledger cards reserved with no plastic yet"
              rows={queue.needsProgramming || []}
            />
            <QueueTable
              title="Flagged for review"
              subtitle="Migration or mismatch — a person decides"
              rows={queue.flaggedForReview || []}
            />
          </>
        ) : (
          <p>Loading queues…</p>
        )}

        <section className="admin-collapsible-section" style={{ marginBottom: 20 }}>
          <div className="admin-collapsible-section__header">
            <div className="admin-collapsible-section__titles">
              <h3 className="admin-collapsible-section__title">Velox card orders</h3>
              <p className="admin-collapsible-section__subtitle">
                Company CardOrder rows with design status and proof. Shopify queues above are unchanged.
              </p>
            </div>
            <span className="admin-collapsible-section__badge">{cardOrders.length}</span>
          </div>
          {cardOrders.length === 0 ? (
            <p className="admin-table-subtitle">No Velox card orders.</p>
          ) : isDesktop ? (
            <div className="admin-table">
              <table>
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Type / qty</th>
                    <th>Payment</th>
                    <th>Design / proof</th>
                    {manageOrders ? <th>Update</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {cardOrders.map((order) => {
                    const id = String(order._id);
                    const draft = orderDrafts[id] || {
                      designStatus: order.designStatus || 'draft',
                      proofImageUrl: order.proof?.imageUrl || '',
                    };
                    return (
                      <tr key={id}>
                        <td>
                          {id}
                          <div className="admin-table-subtitle">
                            {order.enterpriseId ? `Company ${order.enterpriseId}` : 'No company'}
                          </div>
                        </td>
                        <td>{order.orderType || order.cardKind || '—'} · {order.quantity ?? '—'}</td>
                        <td>{order.paymentStatus || '—'}</td>
                        <td>
                          <div>{order.designStatus || '—'}</div>
                          {order.proof?.imageUrl ? (
                            <a href={order.proof.imageUrl} target="_blank" rel="noreferrer">Proof v{order.proof.version || 1}</a>
                          ) : (
                            <span className="admin-table-subtitle">No proof</span>
                          )}
                        </td>
                        {manageOrders ? (
                          <td>
                            <CardOrderEditor
                              order={order}
                              draft={draft}
                              disabled={savingOrderId === id}
                              onChange={(next) => setOrderDrafts((prev) => ({ ...prev, [id]: next }))}
                              onSave={saveCardOrder}
                            />
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
              {cardOrders.map((order) => {
                const id = String(order._id);
                const draft = orderDrafts[id] || {
                  designStatus: order.designStatus || 'draft',
                  proofImageUrl: order.proof?.imageUrl || '',
                };
                return (
                  <div
                    key={id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      padding: '14px 16px',
                      background: 'var(--vx-admin-surface)',
                      border: '1px solid var(--vx-admin-border)',
                      borderRadius: 12,
                    }}
                  >
                    <strong>{id}</strong>
                    <span className="admin-table-subtitle">
                      {order.orderType || order.cardKind || '—'} · qty {order.quantity ?? '—'} · {order.paymentStatus || '—'}
                    </span>
                    <span>{order.designStatus || '—'}</span>
                    {order.proof?.imageUrl ? (
                      <a href={order.proof.imageUrl} target="_blank" rel="noreferrer">Proof v{order.proof.version || 1}</a>
                    ) : (
                      <span className="admin-table-subtitle">No proof</span>
                    )}
                    {manageOrders ? (
                      <CardOrderEditor
                        order={order}
                        draft={draft}
                        disabled={savingOrderId === id}
                        onChange={(next) => setOrderDrafts((prev) => ({ ...prev, [id]: next }))}
                        onSave={saveCardOrder}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
